import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDroppable } from '@dnd-kit/core';
import ScopeCard from './ScopeCard';

/**
 * Wraps a scope card as a drop target for a scratchpad task, highlighting it
 * while a task hovers. Used only when the board is inside a bet's DndContext
 * (assignEnabled); otherwise the card renders bare, so the board stays usable
 * without a DndContext around it.
 */
const TaskDropZone = ({ scopeId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `scope-${scopeId}` });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRadius: 2,
        outline: isOver ? '2px solid' : '2px solid transparent',
        outlineColor: isOver ? 'primary.main' : 'transparent',
        transition: 'outline-color 0.15s ease',
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Every scope of a bet as a card that wraps into a responsive grid, sitting
 * under the hill chart. The hill answers "how is this going"; these answer
 * "what is actually left". Clicking a card opens the scope drawer.
 *
 * The bet's todos are owned by BetCard so the cards here and the drawer share
 * one copy; this board only reads them for the per-card progress badge.
 */
// A manual scope has no Jira key, so its todos are keyed by the scope's own id.
// The server keys the two families the same way, so they share one map without
// collision.
const todoKeyFor = (scope) => scope.jiraKey || scope.id;

const ScopeBoard = ({
  scopes = [],
  selectedScopeId = null,
  onSelectScope,
  todosByScope = null,
  loadingTodos = false,
  error = null,
  onReload,
  onRenameScope,
  manual = false,
  assignEnabled = false,
  members = [],
}) => {
  // Left to right on the hill, so the grid reads in the same order as the chart.
  const ordered = [...scopes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}>
          {`SCOPES · ${ordered.length}`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={manual ? 'Reload todos' : 'Reload todos from Jira'}>
          <span>
            <IconButton size="small" onClick={onReload} disabled={loadingTodos || !onReload}>
              {loadingTodos ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.5,
        }}
      >
        {ordered.map(scope => {
          const card = (
            <ScopeCard
              key={scope.id}
              scope={scope}
              todos={todosByScope?.[todoKeyFor(scope)] ?? null}
              loadingTodos={loadingTodos}
              selected={scope.id === selectedScopeId}
              onClick={() => onSelectScope && onSelectScope(scope)}
              onRename={onRenameScope}
              members={members}
            />
          );
          return assignEnabled
            ? <TaskDropZone key={scope.id} scopeId={scope.id}>{card}</TaskDropZone>
            : card;
        })}
      </Box>
    </Box>
  );
};

export default ScopeBoard;
